import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Edit, Trash2, Star, Copy, Power, Plus, MoreVertical, Settings } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';

interface ScoringModel {
  id: number;
  title: string;
  credit_type: string;
  debtor_type: string;
  is_entrepreneur: boolean;
  status: string;
  is_default: boolean;
  last_updated: string;
}

interface ScoringModelTableProps {
  models: ScoringModel[];
  loading: boolean;
  onDelete: (id: number) => void;
  onDuplicate: (model: ScoringModel) => void;
  onToggleStatus: (model: ScoringModel) => void;
  onSetDefault: (model: ScoringModel) => void;
}

const creditTypeLabels: Record<string, string> = {
  working_capital: 'Modal Kerja',
  investment: 'Investasi',
  consumer: 'Konsumtif',
  kpr: 'KPR',
  kendaraan: 'Kendaraan',
  multiguna: 'Multiguna',
};

const debtorTypeLabels: Record<string, string> = {
  individual: 'Perorangan',
  corporate: 'Badan Usaha',
};

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-red-100 text-red-700',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  active: 'Aktif',
  inactive: 'Nonaktif',
};

export default function ScoringModelTable({
  models,
  loading,
  onDelete,
  onDuplicate,
  onToggleStatus,
  onSetDefault,
}: ScoringModelTableProps) {
  if (loading) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left p-4 font-medium text-gray-600">Judul Model</th>
              <th className="text-left p-4 font-medium text-gray-600">Jenis Kredit</th>
              <th className="text-left p-4 font-medium text-gray-600">Jenis Debitur</th>
              <th className="text-left p-4 font-medium text-gray-600">Wirausaha</th>
              <th className="text-left p-4 font-medium text-gray-600">Status</th>
              <th className="text-left p-4 font-medium text-gray-600">Last Updated</th>
              <th className="text-right p-4 font-medium text-gray-600">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {Array(5)
              .fill(0)
              .map((_, i) => (
                <tr key={i} className="border-b">
                  <td className="p-4">
                    <div className="h-4 w-48 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="p-4">
                    <div className="h-6 w-28 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="p-4">
                    <div className="h-6 w-28 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="p-4">
                    <div className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="p-4">
                    <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="p-4">
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="p-4">
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse ml-auto" />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (models.length === 0) {
    return (
      <div className="text-center py-12">
        <Settings className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="text-gray-500">Tidak ada model scoring</p>
        <Link to="/credit/scoring/new">
          <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-300 flex items-center gap-2 mx-auto">
            <Plus className="w-4 h-4" /> Buat Model Pertama
          </button>
        </Link>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50 border-b">
            <th className="text-left p-4 font-medium text-gray-600">Judul Model</th>
            <th className="text-left p-4 font-medium text-gray-600">Jenis Kredit</th>
            <th className="text-left p-4 font-medium text-gray-600">Jenis Debitur</th>
            <th className="text-left p-4 font-medium text-gray-600">Wirausaha</th>
            <th className="text-left p-4 font-medium text-gray-600">Status</th>
            <th className="text-left p-4 font-medium text-gray-600">Last Updated</th>
            <th className="text-right p-4 font-medium text-gray-600">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {models.map(model => (
            <tr key={model.id} className="border-b hover:bg-gray-50 transition-colors">
              <td className="p-4">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{model.title}</span>
                  {model.is_default && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded flex items-center gap-1">
                      <Star className="w-3 h-3" /> Default
                    </span>
                  )}
                </div>
              </td>
              <td className="p-4">
                <span className="px-2 py-1 text-xs font-medium border rounded">
                  {creditTypeLabels[model.credit_type] || model.credit_type}
                </span>
              </td>
              <td className="p-4">
                <span className="px-2 py-1 text-xs font-medium bg-purple-50 text-purple-700 rounded">
                  {debtorTypeLabels[model.debtor_type] || model.debtor_type}
                </span>
              </td>
              <td className="p-4">
                <span
                  className={`px-2 py-1 text-xs font-medium rounded ${model.is_entrepreneur ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-600'}`}
                >
                  {model.is_entrepreneur ? 'Ya' : 'Tidak'}
                </span>
              </td>
              <td className="p-4">
                <span
                  className={`px-2 py-1 text-xs font-medium rounded ${statusColors[model.status]}`}
                >
                  {statusLabels[model.status]}
                </span>
              </td>
              <td className="p-4 text-sm text-gray-500">
                {model.last_updated ? format(new Date(model.last_updated), 'dd MMM yyyy') : '-'}
              </td>
              <td className="p-4 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-2 hover:bg-gray-100 rounded-lg">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => {
                        window.location.href = `/credit/scoring/edit?id=${model.id}`;
                      }}
                    >
                      <Edit className="w-4 h-4 mr-2" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDuplicate(model)}>
                      <Copy className="w-4 h-4 mr-2" /> Duplikat
                    </DropdownMenuItem>
                    {!model.is_default && (
                      <DropdownMenuItem onClick={() => onSetDefault(model)}>
                        <Star className="w-4 h-4 mr-2" /> Set sebagai Default
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => onToggleStatus(model)}>
                      <Power className="w-4 h-4 mr-2" />
                      {model.status === 'active' ? 'Nonaktifkan' : 'Aktifkan'}
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-red-600" onClick={() => onDelete(model.id)}>
                      <Trash2 className="w-4 h-4 mr-2" /> Hapus
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
